/// <reference types="vite/client" />
// ─── API Utility ─────────────────────────────────────────────────────────────
// All fetch wrappers automatically attach the Cloudflare Access JWT header.
// In production, the CF Access cookie is injected by the browser after the
// user passes the Access policy — the JWT is available via document.cookie.

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

/**
 * Get the Clerk authentication token if a user is signed in.
 * Returns an empty string if not present.
 */
async function getAuthToken(): Promise<string> {
  // @ts-ignore - Clerk is injected globally by ClerkProvider
  if (window.Clerk && window.Clerk.session) {
    // @ts-ignore
    return (await window.Clerk.session.getToken()) ?? ''
  }
  return ''
}

/**
 * Build standard auth headers for all admin API requests.
 */
async function authHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const token = await getAuthToken()
  return {
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra,
  }
}

/**
 * Generic JSON fetch wrapper with auth headers.
 */
async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${path}`
  const headers = {
    ...(await authHeaders()),
    ...(options.headers as Record<string, string>),
  }

  const res = await fetch(url, { ...options, headers })

  if (!res.ok) {
    const body = await res.text()
    let message = `API Error ${res.status}`
    try {
      const json = JSON.parse(body)
      message = json.error ?? json.message ?? message
    } catch {
      message = body || message
    }
    throw new Error(message)
  }

  return res.json() as Promise<T>
}

// ─── Articles ─────────────────────────────────────────────────────────────────

export type Article = {
  id: string
  slug: string
  title: string
  category: string | null
  tags: string | null
  markdown_content?: string
  meta_title: string | null
  meta_description: string | null
  is_published: number
  created_at: string
  updated_at: string
}

export type Revision = {
  id: string
  saved_at: string
  markdown_content?: string
}

export const articlesApi = {
  list: () => apiFetch<{ articles: Article[] }>('/api/admin/articles'),

  get: (id: string) =>
    apiFetch<{ article: Article }>(`/api/admin/articles/${id}`),

  save: (data: Partial<Article> & { markdown_content: string }) =>
    apiFetch<{ success: boolean; id: string }>('/api/admin/articles', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/api/admin/articles/${id}`, { method: 'DELETE' }),

  listRevisions: (id: string) =>
    apiFetch<{ revisions: Revision[] }>(`/api/admin/articles/${id}/revisions`),

  getRevision: (articleId: string, revId: string) =>
    apiFetch<{ revision: Revision }>(
      `/api/admin/articles/${articleId}/revisions/${revId}`
    ),
}

// ─── Media ────────────────────────────────────────────────────────────────────

export type MediaFile = {
  key: string
  size: number
  uploaded: string | null
  url: string
  markdownEmbed: string
  etag: string
  customMetadata?: Record<string, string>
}

export const mediaApi = {
  list: () => apiFetch<{ files: MediaFile[]; count: number }>('/api/admin/files'),

  upload: async (file: File, folder: string = 'general'): Promise<{ url: string; key: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)

    const res = await fetch(`${API_BASE}/api/admin/upload`, {
      method: 'POST',
      headers: await authHeaders(),
      body: formData,
    })

    if (!res.ok) {
      const body = (await res.json()) as { error: string }
      throw new Error(body.error ?? `Upload failed: ${res.status}`)
    }

    return res.json()
  },

  update: (data: { oldKey: string; newKey: string; altText: string }) =>
    apiFetch<{ success: boolean; key: string; url: string }>('/api/admin/files', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  delete: (key: string) =>
    apiFetch<{ success: boolean }>(`/api/admin/files/${key}`, { method: 'DELETE' }),
}

// ─── Feature Flags ────────────────────────────────────────────────────────────

export type Flag = {
  key: string
  value: string
  description: string | null
  updated_at: string
}

export const flagsApi = {
  list: () => apiFetch<{ flags: Flag[] }>('/api/admin/flags'),

  save: (flag: { key: string; value: string; description?: string }) =>
    apiFetch<{ success: boolean }>('/api/admin/flags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(flag),
    }),

  delete: (key: string) =>
    apiFetch<{ success: boolean }>(`/api/admin/flags/${key}`, { method: 'DELETE' }),
}

// ─── Redirects ────────────────────────────────────────────────────────────────

export type Redirect = {
  source_url: string
  target_url: string
  created_at: string
}

export const redirectsApi = {
  list: () => apiFetch<{ redirects: Redirect[] }>('/api/admin/redirects'),

  save: (data: { source_url: string; target_url: string }) =>
    apiFetch<{ success: boolean }>('/api/admin/redirects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  delete: (source_url: string) =>
    apiFetch<{ success: boolean }>('/api/admin/redirects', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_url }),
    }),
}

// ─── Cache Purge ─────────────────────────────────────────────────────────────

export const cacheApi = {
  purge: () =>
    apiFetch<{ success: boolean; message: string }>('/api/admin/purge-cache', {
      method: 'POST',
    }),
}
